// =====================================================
// ATTENDANCE SERVICE
// =====================================================
// Service layer for church attendance management
// Handles services, check-ins, absences, and welfare checks
// =====================================================

import { supabase } from '@/lib/supabase'

// =====================================================
// TYPES
// =====================================================

export interface ChurchService {
  id: string
  church_id: string
  service_type: string
  service_date: string
  service_time: string
  service_name: string
  description?: string
  check_in_code: string
  check_in_window_start: string
  check_in_window_end: string
  expected_attendance?: number
  actual_attendance: number
  is_active: boolean
  created_at: string
  // Service detail fields
  theme?: string
  sermon_title?: string
  sermon_speaker?: string
  sermon_text?: string // Main sermon text/verse reference (e.g., "John 3:16")
  scripture_readings?: Array<{
    reference: string
    testament?: string
  }>
  worship_songs?: string[]
  special_announcements?: string
  service_image_url?: string
  livestream_url?: string
  duration_minutes?: number
  // Self check-in settings
  allow_self_checkin?: boolean
  require_location_verification?: boolean
  church_location_lat?: number
  church_location_lng?: number
  max_distance_meters?: number
  send_post_service_reminder?: boolean
  reminder_hours_after?: number
}

export interface AttendanceRecord {
  id: string
  service_id: string
  member_id: string
  church_id: string
  check_in_method: 'qr_code' | 'manual' | 'nfc' | 'location' | 'usher'
  check_in_source?: 'qr_scan' | 'manual_app' | 'manual_admin' | 'post_service' | 'nfc_tap' | 'location_auto'
  check_in_time: string
  check_in_location_lat?: number
  check_in_location_lng?: number
  device_info?: any
  checked_in_by?: string
  notes?: string
  created_at: string
  // Relations
  member?: {
    id: string
    preferred_name: string
    phone_number?: string
    photo_url?: string
  }
}

export interface AbsenceRecord {
  id: string
  member_id: string
  church_id: string
  service_id: string
  absence_date: string
  consecutive_absences: number
  is_excused: boolean
  excuse_reason?: string
  welfare_check_needed: boolean
  welfare_check_triggered: boolean
  created_at: string
  // Relations
  member?: {
    id: string
    preferred_name: string
    phone_number?: string
    photo_url?: string
  }
  service?: {
    service_name: string
    service_date: string
    service_time: string
  }
}

export interface WelfareCheck {
  id: string
  member_id: string
  church_id: string
  triggered_by: string
  trigger_details?: any
  assigned_to?: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  contact_method?: 'phone' | 'visit' | 'text' | 'email' | 'video_call'
  scheduled_date?: string
  contact_date?: string
  contact_notes?: string
  outcome?: string
  outcome_details?: string
  follow_up_needed: boolean
  follow_up_date?: string
  created_at: string
  updated_at: string
  completed_at?: string
  // Relations
  member?: {
    id: string
    preferred_name: string
    phone_number?: string
    photo_url?: string
    consecutive_absences: number
  }
  assigned_member?: {
    id: string
    preferred_name: string
  }
}

export interface ServiceAttendanceStats {
  total_expected: number
  total_attended: number
  total_absent: number
  attendance_rate: number
  check_in_sources: {
    qr_scan: number
    manual_app: number
    manual_admin: number
    post_service: number
  }
}

export interface MemberAttendanceStats {
  member_id: string
  member_name: string
  total_services: number
  total_attendance: number
  attendance_rate: number
  consecutive_absences: number
  last_attendance_date?: string
  current_streak: number
}

// =====================================================
// ATTENDANCE SERVICE CLASS
// =====================================================

class AttendanceService {
  // =====================================================
  // SERVICE MANAGEMENT
  // =====================================================

  /**
   * Create a new church service
   */
  async createService(
    churchId: string,
    serviceData: Partial<ChurchService>
  ): Promise<ChurchService> {
    try {
      // Generate unique check-in code (6 digits)
      const checkInCode = Math.random().toString(36).substring(2, 8).toUpperCase()

      const { data, error } = await supabase
        .from('church_services')
        .insert({
          church_id: churchId,
          check_in_code: checkInCode,
          actual_attendance: 0,
          is_active: true,
          ...serviceData
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error: any) {
      console.error('Error creating service:', error)
      throw new Error(`Failed to create service: ${error.message}`)
    }
  }

  /**
   * Update an existing service
   */
  async updateService(
    serviceId: string,
    updates: Partial<ChurchService>
  ): Promise<ChurchService> {
    try {
      const { data, error } = await supabase
        .from('church_services')
        .update(updates)
        .eq('id', serviceId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error: any) {
      console.error('Error updating service:', error)
      throw new Error(`Failed to update service: ${error.message}`)
    }
  }

  /**
   * Delete a service
   */
  async deleteService(serviceId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('church_services')
        .delete()
        .eq('id', serviceId)

      if (error) throw error
    } catch (error: any) {
      console.error('Error deleting service:', error)
      throw new Error(`Failed to delete service: ${error.message}`)
    }
  }

  /**
   * Get all services for a church (with pagination)
   * Now includes actual attendance count from attendance_records
   */
  async getServices(
    churchId: string,
    options?: {
      from?: number
      to?: number
      isActive?: boolean
      startDate?: string
      endDate?: string
    }
  ): Promise<ChurchService[]> {
    try {
      let query = supabase
        .from('church_services')
        .select('*')
        .eq('church_id', churchId)
        .order('service_date', { ascending: false })
        .order('service_time', { ascending: false })

      if (options?.isActive !== undefined) {
        query = query.eq('is_active', options.isActive)
      }

      if (options?.startDate) {
        query = query.gte('service_date', options.startDate)
      }

      if (options?.endDate) {
        query = query.lte('service_date', options.endDate)
      }

      if (options?.from !== undefined && options?.to !== undefined) {
        query = query.range(options.from, options.to)
      }

      const { data, error } = await query

      if (error) throw error

      // Fetch actual attendance counts from attendance_records
      if (data && data.length > 0) {
        const serviceIds = data.map(s => s.id)
        const { data: attendanceCounts, error: countError } = await supabase
          .from('attendance_records')
          .select('service_id')
          .in('service_id', serviceIds)

        if (!countError && attendanceCounts) {
          // Count records per service
          const countMap: Record<string, number> = {}
          for (const record of attendanceCounts) {
            countMap[record.service_id] = (countMap[record.service_id] || 0) + 1
          }

          // Update actual_attendance with real counts
          for (const service of data) {
            service.actual_attendance = countMap[service.id] || 0
          }
        }
      }

      return data || []
    } catch (error: any) {
      console.error('Error fetching services:', error)
      throw new Error(`Failed to fetch services: ${error.message}`)
    }
  }

  /**
   * Get a single service by ID
   * Now includes actual attendance count from attendance_records
   */
  async getService(serviceId: string): Promise<ChurchService | null> {
    try {
      const { data, error } = await supabase
        .from('church_services')
        .select('*')
        .eq('id', serviceId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') return null
        throw error
      }

      // Get actual attendance count from attendance_records
      if (data) {
        const { count, error: countError } = await supabase
          .from('attendance_records')
          .select('*', { count: 'exact', head: true })
          .eq('service_id', serviceId)

        if (!countError) {
          data.actual_attendance = count || 0
        }
      }

      return data
    } catch (error: any) {
      console.error('Error fetching service:', error)
      throw new Error(`Failed to fetch service: ${error.message}`)
    }
  }

  /**
   * Get upcoming services
   */
  async getUpcomingServices(
    churchId: string,
    days: number = 30
  ): Promise<ChurchService[]> {
    try {
      const today = new Date().toISOString().split('T')[0]
      const futureDate = new Date()
      futureDate.setDate(futureDate.getDate() + days)
      const futureDateStr = futureDate.toISOString().split('T')[0]

      const { data, error } = await supabase
        .from('church_services')
        .select('*')
        .eq('church_id', churchId)
        .gte('service_date', today)
        .lte('service_date', futureDateStr)
        .eq('is_active', true)
        .order('service_date', { ascending: true })
        .order('service_time', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error: any) {
      console.error('Error fetching upcoming services:', error)
      return []
    }
  }

  /**
   * Get today's services
   */
  async getTodaysServices(churchId: string): Promise<ChurchService[]> {
    try {
      const today = new Date().toISOString().split('T')[0]

      const { data, error } = await supabase
        .from('church_services')
        .select('*')
        .eq('church_id', churchId)
        .eq('service_date', today)
        .eq('is_active', true)
        .order('service_time', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error: any) {
      console.error('Error fetching today\'s services:', error)
      return []
    }
  }

  // =====================================================
  // ATTENDANCE TRACKING
  // =====================================================

  /**
   * Get attendance records for a service
   */
  async getServiceAttendance(serviceId: string): Promise<AttendanceRecord[]> {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .select(`
          *,
          member:members (
            id,
            preferred_name,
            phone_number,
            photo_url
          )
        `)
        .eq('service_id', serviceId)
        .order('check_in_time', { ascending: true })

      if (error) throw error
      return data || []
    } catch (error: any) {
      console.error('Error fetching service attendance:', error)
      return []
    }
  }

  /**
   * Get attendance statistics for a service
   */
  async getServiceStats(serviceId: string, churchId: string): Promise<ServiceAttendanceStats> {
    try {
      // Get service details
      const service = await this.getService(serviceId)

      // Get attendance records
      const attendance = await this.getServiceAttendance(serviceId)

      // Get total active members
      const { count: totalMembers } = await supabase
        .from('members')
        .select('*', { count: 'exact', head: true })
        .eq('church_id', churchId)
        .eq('is_active', true)
        .eq('member_status', 'active')

      const totalExpected = service?.expected_attendance || totalMembers || 0
      const totalAttended = attendance.length
      const totalAbsent = totalExpected - totalAttended
      const attendanceRate = totalExpected > 0 ? (totalAttended / totalExpected) * 100 : 0

      // Count by source
      const checkInSources = {
        qr_scan: 0,
        manual_app: 0,
        manual_admin: 0,
        post_service: 0
      }

      for (const record of attendance) {
        const source = record.check_in_source || 'qr_scan'
        if (source in checkInSources) {
          checkInSources[source as keyof typeof checkInSources]++
        }
      }

      return {
        total_expected: totalExpected,
        total_attended: totalAttended,
        total_absent: totalAbsent,
        attendance_rate: Math.round(attendanceRate),
        check_in_sources: checkInSources
      }
    } catch (error: any) {
      console.error('Error calculating service stats:', error)
      return {
        total_expected: 0,
        total_attended: 0,
        total_absent: 0,
        attendance_rate: 0,
        check_in_sources: { qr_scan: 0, manual_app: 0, manual_admin: 0, post_service: 0 }
      }
    }
  }

  /**
   * Manual check-in by admin
   */
  async manualCheckIn(
    serviceId: string,
    memberId: string,
    churchId: string,
    checkedInBy: string,
    notes?: string
  ): Promise<AttendanceRecord> {
    try {
      const { data, error } = await supabase
        .from('attendance_records')
        .insert({
          service_id: serviceId,
          member_id: memberId,
          church_id: churchId,
          check_in_method: 'manual',
          check_in_source: 'manual_admin',
          checked_in_by: checkedInBy,
          notes
        })
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error: any) {
      console.error('Error with manual check-in:', error)
      throw new Error(`Failed to check in member: ${error.message}`)
    }
  }

  /**
   * Bulk check-in multiple members
   */
  async bulkCheckIn(
    serviceId: string,
    memberIds: string[],
    churchId: string,
    checkedInBy: string
  ): Promise<number> {
    try {
      const records = memberIds.map(memberId => ({
        service_id: serviceId,
        member_id: memberId,
        church_id: churchId,
        check_in_method: 'manual' as const,
        check_in_source: 'manual_admin' as const,
        checked_in_by: checkedInBy
      }))

      const { data, error } = await supabase
        .from('attendance_records')
        .insert(records)
        .select()

      if (error) throw error
      return data?.length || 0
    } catch (error: any) {
      console.error('Error with bulk check-in:', error)
      throw new Error(`Failed to bulk check-in: ${error.message}`)
    }
  }

  /**
   * Remove check-in (undo)
   */
  async removeCheckIn(attendanceRecordId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('attendance_records')
        .delete()
        .eq('id', attendanceRecordId)

      if (error) throw error
    } catch (error: any) {
      console.error('Error removing check-in:', error)
      throw new Error(`Failed to remove check-in: ${error.message}`)
    }
  }

  // =====================================================
  // ABSENCE MANAGEMENT
  // =====================================================

  /**
   * Get absence records for a church
   */
  async getAbsences(
    churchId: string,
    options?: {
      startDate?: string
      endDate?: string
      memberId?: string
      needsWelfareCheck?: boolean
    }
  ): Promise<AbsenceRecord[]> {
    try {
      let query = supabase
        .from('absence_records')
        .select(`
          *,
          member:members (
            id,
            preferred_name,
            phone_number,
            photo_url
          ),
          service:church_services (
            service_name,
            service_date,
            service_time
          )
        `)
        .eq('church_id', churchId)
        .order('absence_date', { ascending: false })

      if (options?.startDate) {
        query = query.gte('absence_date', options.startDate)
      }

      if (options?.endDate) {
        query = query.lte('absence_date', options.endDate)
      }

      if (options?.memberId) {
        query = query.eq('member_id', options.memberId)
      }

      if (options?.needsWelfareCheck !== undefined) {
        query = query.eq('welfare_check_needed', options.needsWelfareCheck)
      }

      const { data, error } = await query

      if (error) throw error
      return data || []
    } catch (error: any) {
      console.error('Error fetching absences:', error)
      return []
    }
  }

  /**
   * Create absence records after a service
   */
  async createAbsenceRecords(serviceId: string): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('create_absence_records_for_service', {
        service_uuid: serviceId
      })

      if (error) throw error
      return data || 0
    } catch (error: any) {
      console.error('Error creating absence records:', error)
      throw new Error(`Failed to create absence records: ${error.message}`)
    }
  }

  /**
   * Mark absence as excused
   */
  async excuseAbsence(
    absenceId: string,
    reason: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('absence_records')
        .update({
          is_excused: true,
          excuse_reason: reason
        })
        .eq('id', absenceId)

      if (error) throw error
    } catch (error: any) {
      console.error('Error excusing absence:', error)
      throw new Error(`Failed to excuse absence: ${error.message}`)
    }
  }

  // =====================================================
  // WELFARE CHECKS
  // =====================================================

  /**
   * Get welfare checks for a church
   */
  async getWelfareChecks(
    churchId: string,
    status?: WelfareCheck['status']
  ): Promise<WelfareCheck[]> {
    try {
      let query = supabase
        .from('welfare_checks')
        .select(`
          *,
          member:members!welfare_checks_member_id_fkey (
            id,
            preferred_name,
            phone_number,
            photo_url,
            consecutive_absences
          ),
          assigned_member:members!welfare_checks_assigned_to_fkey (
            id,
            preferred_name
          )
        `)
        .eq('church_id', churchId)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: true })

      if (status) {
        query = query.eq('status', status)
      }

      const { data, error } = await query

      if (error) throw error
      return data || []
    } catch (error: any) {
      console.error('Error fetching welfare checks:', error)
      return []
    }
  }

  /**
   * Trigger automatic welfare checks
   */
  async triggerAutoWelfareChecks(): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('auto_create_welfare_checks')

      if (error) throw error
      return data || 0
    } catch (error: any) {
      console.error('Error triggering welfare checks:', error)
      throw new Error(`Failed to trigger welfare checks: ${error.message}`)
    }
  }

  /**
   * Create a welfare check manually
   */
  async createWelfareCheck(
    memberId: string,
    churchId: string,
    triggeredBy: string,
    data: Partial<WelfareCheck>
  ): Promise<WelfareCheck> {
    try {
      const { data: welfareCheck, error } = await supabase
        .from('welfare_checks')
        .insert({
          member_id: memberId,
          church_id: churchId,
          triggered_by: triggeredBy,
          status: 'pending',
          priority: 'medium',
          follow_up_needed: false,
          ...data
        })
        .select()
        .single()

      if (error) throw error
      return welfareCheck
    } catch (error: any) {
      console.error('Error creating welfare check:', error)
      throw new Error(`Failed to create welfare check: ${error.message}`)
    }
  }

  /**
   * Update welfare check
   */
  async updateWelfareCheck(
    welfareCheckId: string,
    updates: Partial<WelfareCheck>
  ): Promise<WelfareCheck> {
    try {
      const { data, error } = await supabase
        .from('welfare_checks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', welfareCheckId)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error: any) {
      console.error('Error updating welfare check:', error)
      throw new Error(`Failed to update welfare check: ${error.message}`)
    }
  }

  /**
   * Complete welfare check
   */
  async completeWelfareCheck(
    welfareCheckId: string,
    outcome: string,
    outcomeDetails: string,
    followUpNeeded: boolean = false,
    followUpDate?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('welfare_checks')
        .update({
          status: 'completed',
          outcome,
          outcome_details: outcomeDetails,
          follow_up_needed: followUpNeeded,
          follow_up_date: followUpDate,
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', welfareCheckId)

      if (error) throw error
    } catch (error: any) {
      console.error('Error completing welfare check:', error)
      throw new Error(`Failed to complete welfare check: ${error.message}`)
    }
  }

  // =====================================================
  // ANALYTICS & REPORTS
  // =====================================================

  /**
   * Get member attendance statistics
   */
  async getMemberStats(
    churchId: string,
    memberId: string
  ): Promise<MemberAttendanceStats | null> {
    try {
      // Get member info
      const { data: member } = await supabase
        .from('members')
        .select('preferred_name, consecutive_absences, last_attendance_date')
        .eq('id', memberId)
        .single()

      if (!member) return null

      // Get total services in past 90 days
      const ninetyDaysAgo = new Date()
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
      const ninetyDaysAgoStr = ninetyDaysAgo.toISOString().split('T')[0]

      const { count: totalServices } = await supabase
        .from('church_services')
        .select('*', { count: 'exact', head: true })
        .eq('church_id', churchId)
        .gte('service_date', ninetyDaysAgoStr)
        .eq('is_active', true)

      // Get member's attendance
      const { count: totalAttendance } = await supabase
        .from('attendance_records')
        .select('*', { count: 'exact', head: true })
        .eq('member_id', memberId)
        .eq('church_id', churchId)
        .gte('check_in_time', ninetyDaysAgoStr)

      const attendanceRate = (totalServices || 0) > 0
        ? ((totalAttendance || 0) / (totalServices || 0)) * 100
        : 0

      return {
        member_id: memberId,
        member_name: member.preferred_name,
        total_services: totalServices || 0,
        total_attendance: totalAttendance || 0,
        attendance_rate: Math.round(attendanceRate),
        consecutive_absences: member.consecutive_absences || 0,
        last_attendance_date: member.last_attendance_date,
        current_streak: totalAttendance || 0
      }
    } catch (error: any) {
      console.error('Error fetching member stats:', error)
      return null
    }
  }

  /**
   * Get overall attendance trends
   */
  async getAttendanceTrends(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<Array<{ date: string; count: number; service_name: string }>> {
    try {
      const { data, error } = await supabase
        .from('church_services')
        .select('service_date, service_name, actual_attendance')
        .eq('church_id', churchId)
        .gte('service_date', startDate)
        .lte('service_date', endDate)
        .order('service_date', { ascending: true })

      if (error) throw error

      return (data || []).map(service => ({
        date: service.service_date,
        count: service.actual_attendance,
        service_name: service.service_name
      }))
    } catch (error: any) {
      console.error('Error fetching attendance trends:', error)
      return []
    }
  }
}

// =====================================================
// EXPORT SINGLETON INSTANCE
// =====================================================

export const attendanceService = new AttendanceService()

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Generate QR code data URL for a service
 */
export async function generateServiceQRCode(
  checkInCode: string
): Promise<string> {
  try {
    // Use qrcode library (needs to be installed: npm install qrcode)
    const QRCode = await import('qrcode')
    const qrDataUrl = await QRCode.toDataURL(checkInCode, {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    })
    return qrDataUrl
  } catch (error) {
    console.error('Error generating QR code:', error)
    throw new Error('Failed to generate QR code')
  }
}

/**
 * Format service date and time for display
 */
export function formatServiceDateTime(date: string, time: string): string {
  const serviceDate = new Date(date)
  const [hours, minutes] = time.split(':')
  const hour = parseInt(hours)
  const ampm = hour >= 12 ? 'PM' : 'AM'
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour

  return `${serviceDate.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })} at ${displayHour}:${minutes} ${ampm}`
}

/**
 * Check if service is today
 */
export function isToday(dateString: string): boolean {
  const today = new Date().toISOString().split('T')[0]
  return dateString === today
}

/**
 * Check if check-in window is currently open
 */
export function isCheckInWindowOpen(service: ChurchService): boolean {
  const now = new Date()
  const windowStart = new Date(service.check_in_window_start)
  const windowEnd = new Date(service.check_in_window_end)
  return now >= windowStart && now <= windowEnd
}
